/**
 * 일반 스크롤 컨테이너에서 무한 스크롤을 처리하기 위한 공통 컴포저블
 *
 * - 스크롤이 하단에 근접했을 때 `loadMore` 콜백을 호출합니다.
 * - 컨테이너의 내용 높이가 뷰포트보다 작을 경우, 자동으로 데이터를 추가 로드하여
 *   스크롤 바가 생길 때까지 반복할 수 있는 유틸리티를 제공합니다.
 */
import { ref } from 'vue';
/**
 * 일반 스크롤 컨테이너용 무한 스크롤 컴포저블
 *
 * @param options - 설정 옵션
 * @returns 스크롤 핸들러 및 자동 채움 함수
 */
export function useInfiniteScrollContainer(options) {
    const isChecking = ref(false);
    const handleScroll = async (event) => {
        const { hasMore, isLoading, loadMore } = options;
        const target = event.target;
        if (!target || !hasMore.value || isLoading.value)
            return;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;
        const threshold = options.threshold ?? 100;
        if (scrollHeight - scrollTop - clientHeight < threshold) {
            await loadMore();
        }
    };
    const checkAndFill = async () => {
        const { containerRef, hasMore, isLoading, loadMore } = options;
        if (!containerRef.value || isChecking.value || isLoading.value || !hasMore.value)
            return;
        isChecking.value = true;
        try {
            // 컨테이너에 스크롤이 생길 때까지 반복
            // (무한 루프 방지를 위해 hasMore 값에 의존)
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { scrollHeight, clientHeight } = containerRef.value;
                if (scrollHeight > clientHeight || !hasMore.value) {
                    break;
                }
                await loadMore();
                // 렌더링 반영 대기 (간단한 타임아웃으로 처리)
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
        }
        finally {
            isChecking.value = false;
        }
    };
    return {
        handleScroll,
        checkAndFill,
    };
}
//# sourceMappingURL=useInfiniteScrollContainer.js.map